import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import TimePicker from 'react-time-picker';

import Icon from '@components/Icon/Icon';
import InputLabel from '@components/InputLabel/InputLabel';
import { VerticalFlex } from '@components/RecordCard/RecordCard.style';
import RoundedButton from '@components/RoundedButton';
import TextInputBox from '@components/TextInputBox';

import { body } from '@constants/fonts';

import type { TimeSlot } from '@models/routine';

import { capitalize } from '@utils/format';

import {
  ErrorMessage,
  TimeStyle,
  TimeSlotInputStyle,
  SlotsWrapper,
  SlotWrapper,
} from './TimeSlotInput.style';

const messages = defineMessages({
  add: {
    id: 'timeSlotInput.add',
    defaultMessage: 'Add another time',
  },
  delete: {
    id: 'timeSlotInput.delete',
    defaultMessage: 'Remove',
  },
  from: {
    id: 'timeSlotInput.from',
    defaultMessage: 'from',
  },
  to: {
    id: 'timeSlotInput.to',
    defaultMessage: 'to',
  },
  duration: {
    id: 'timeSlotInput.duration',
    defaultMessage: 'Duration',
  },
  minutes: {
    id: 'timeSlotInput.minutes',
    defaultMessage: 'Minutes',
  },
});

export default function TimeSlotInput({
  error = '',
  onChange,
  required = false,
  timeSlots = [],
  duration: initialDuration = 15,
  ...props
}: {
  /**
   * Error message to display underneath the component
   */
  error?: string;
  /**
   * Text to display above the component
   */
  label?: string;
  /**
   * Callback for when any of the time picker components have changed
   */
  onChange?: (value: TimeSlot[], duration: number) => void;
  /**
   * Indicates whether filling out the component is required
   */
  required?: boolean;
  /**
   * Times that the routine happens at
   */
  timeSlots?: TimeSlot[];
  /**
   * Duration of each routine occurrence
   */
  duration?: number;
}) {
  const { formatMessage } = useIntl();
  const [numRows, setNumRows] = useState(Math.max(timeSlots.length, 1));
  const [duration, setDuration] = useState(initialDuration);
  const { palette } = useTheme();

  const handleTimeSlotChange = (value: string | null, index: number) => {
    let time = null;
    if (value) {
      time = new Date();
      const splitTime = value.split(':');
      time.setHours(Number(splitTime[0]));
      time.setMinutes(Number(splitTime[1]));
      time.setSeconds(0);
      time.setMilliseconds(0);
    }
    if (timeSlots.length && onChange && time) {
      const newSlots = [...timeSlots];
      newSlots[index] = {
        endTime: dayjs(time).add(duration, 'minutes').toDate(),
        startTime: time,
      };
      onChange(newSlots, duration);
    }
  };

  const handleDurationChange = (value: string | number) => {
    let newDuration = 0;
    if (typeof value === 'string') {
      newDuration = parseInt(value, 10);
    } else {
      newDuration = value;
    }
    const newSlots = [...timeSlots].map<TimeSlot>((slot) => {
      const endTime = new Date(slot.startTime);
      endTime.setMinutes(endTime.getMinutes() + newDuration);
      return {
        startTime: slot.startTime,
        endTime,
      };
    });
    setDuration(newDuration);
    onChange?.(newSlots, newDuration);
  };

  const handleTimeSlotDelete = (index: number) => {
    if (index >= 0 && index < timeSlots.length && onChange) {
      const newSlots = [...timeSlots];
      newSlots.splice(index, 1);
      onChange(newSlots, duration);
      setNumRows(numRows - 1);
    }
  };

  useEffect(() => {
    setNumRows(Math.max(timeSlots.length, 1));
  }, [timeSlots]);

  return (
    <TimeSlotInputStyle>
      <SlotsWrapper>
        {Array.from(Array(numRows).keys())
          .map((_value, index) => {
            const timeSlot = timeSlots.at(index);
            if (!timeSlot) {
              return {
                startTime: undefined,
                endTime: undefined,
              };
            }
            return {
              startTime: `${String(timeSlot.startTime.getHours()).padStart(
                2,
                '0'
              )}:${String(timeSlot.startTime.getMinutes()).padStart(2, '0')}`,
              endTime: `${String(timeSlot.endTime.getHours()).padStart(
                2,
                '0'
              )}:${String(timeSlot.endTime.getMinutes()).padStart(2, '0')}`,
            };
          })
          .map((slot, index) => (
            <SlotWrapper key={`time-slot-input-${index}`}>
              <div style={{ display: 'flex' }}>
                <VerticalFlex alignItems="flex-start">
                  <InputLabel
                    label={capitalize(formatMessage(messages.from))}
                    style={{ mr: 1 }}
                  />
                </VerticalFlex>
                <VerticalFlex alignItems="flex-start">
                  <TimeStyle className={error ? 'error' : ''}>
                    <TimePicker
                      {...props}
                      clearIcon={
                        <Icon
                          color="text.primary"
                          name="trash"
                          onClick={() => {
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            if (timeSlots[index] === undefined) {
                              setNumRows(numRows - 1);
                            } else {
                              handleTimeSlotDelete(index);
                            }
                          }}
                          style={
                            numRows > 1
                              ? {}
                              : {
                                  opacity: 0.5,
                                  pointerEvents: 'none',
                                }
                          }
                        />
                      }
                      clockIcon={null}
                      disableClock
                      onChange={(value) => handleTimeSlotChange(value, index)}
                      required={required}
                      value={slot.startTime}
                    />
                  </TimeStyle>
                </VerticalFlex>
              </div>
            </SlotWrapper>
          ))}
      </SlotsWrapper>
      <RoundedButton
        autoSize
        disabled={!timeSlots.length}
        height={30}
        onClick={() => {
          setNumRows(numRows + 1);
        }}
        variant="white"
      >
        {formatMessage(messages.add)}
      </RoundedButton>
      <div style={{ display: 'flex' }}>
        <VerticalFlex alignItems="flex-start">
          <TextInputBox
            label={formatMessage(messages.duration)}
            required={required}
            type="number"
            onChange={(e) => handleDurationChange(e.target.value)}
            style={{
              fontFamily: `${body}`,
              fontSize: '16px',
              paddingTop: '5px',
              color: palette.text.primary,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: palette.background.default,
              width: '157px',
            }}
            value={duration}
          />
        </VerticalFlex>
        <VerticalFlex alignItems="flex-start" style={{ paddingTop: '22px' }}>
          <InputLabel
            label={capitalize(formatMessage(messages.minutes))}
            style={{ ml: 1 }}
          />
        </VerticalFlex>
      </div>
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}
    </TimeSlotInputStyle>
  );
}
